import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ProgressBar, PullDownIcon } from "./ui";

/** How far the content must be dragged before letting go refreshes. */
const PULL_THRESHOLD = 64;
/** Past this the finger stops moving the content — the pull is already committed. */
const PULL_MAX = 96;

/**
 * Pull-to-refresh, the way a phone expects it: drag down from the top and let go.
 *
 * Shared by the schedule list and the parts tab so the gesture and its indicator match.
 * The indicator is a progress bar rather than a spinner, so a pull looks like the
 * same loading the sticky header shows — determinate while the finger is deciding,
 * indeterminate once the refetch is running.
 */
export type PullToRefreshState = {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  pull: number;
  refreshing: boolean;
};

export const usePullToRefresh = (onRefresh: () => Promise<unknown>): PullToRefreshState => {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // The touch handlers are native listeners and would otherwise close over stale
  // state, so the drag's own numbers live in refs.
  const pullRef = useRef(0);
  const startYRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setPullDistance = useCallback((distance: number) => {
    pullRef.current = distance;
    setPull(distance);
  }, []);

  /**
   * Native listeners rather than React's, because `touchmove` has to be non-passive to
   * cancel the browser's own overscroll — React attaches passive listeners and
   * preventDefault there is a no-op. Only engages at the very top of the page, so it
   * can never fight an ordinary scroll, and only on a downward drag, so a tap on a
   * header still does what it looks like it does.
   */
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;

    const onTouchStart = (event: TouchEvent) => {
      if (window.scrollY > 0 || refreshing) {
        startYRef.current = null;
        return;
      }
      startYRef.current = event.touches[0]?.clientY ?? null;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (startYRef.current === null) return;
      const delta = (event.touches[0]?.clientY ?? 0) - startYRef.current;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }
      // Halved, and capped: the content should feel resisted rather than dragged loose.
      const distance = Math.min(delta * 0.5, PULL_MAX);
      setPullDistance(distance);
      if (distance > 2 && event.cancelable) event.preventDefault();
    };

    const onTouchEnd = async () => {
      if (startYRef.current === null) return;
      startYRef.current = null;
      if (pullRef.current < PULL_THRESHOLD) {
        setPullDistance(0);
        return;
      }
      setRefreshing(true);
      setPullDistance(PULL_THRESHOLD);
      try {
        await onRefresh();
      } catch {
        // A failed refetch still has to release the indicator; the list keeps whatever
        // it already had rather than emptying.
      } finally {
        setRefreshing(false);
        setPullDistance(0);
      }
    };

    node.addEventListener("touchstart", onTouchStart, { passive: true });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd);
    node.addEventListener("touchcancel", onTouchEnd);
    return () => {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onRefresh, refreshing, setPullDistance]);

  return { containerRef, pull, refreshing };
};

/**
 * The indicator, rendered in the page's sticky header rather than in the content —
 * otherwise it scrolls away with the list it is reporting on. Shares the slot with the
 * page's own loading bar, so a pull and a load look identical.
 */
export const PullToRefreshBar: React.FC<{ state: PullToRefreshState; loading?: boolean }> = ({ state, loading }) => {
  const { pull, refreshing } = state;
  if (loading || refreshing) return <ProgressBar />;
  if (pull <= 0) return null;
  return <ProgressBar value={Math.min(100, (pull / PULL_THRESHOLD) * 100)} />;
};

/**
 * The draggable region. Grows with the pull, so content is pushed down as you drag.
 *
 * The arrow is the hint that a gesture is happening at all — the progress bar sits up
 * in the sticky header where it reads as loading, which is not the same message. It
 * fades in with the drag and flips to point up once far enough to let go, so the
 * commit point is visible rather than guessed at.
 */
export const PullToRefreshArea: React.FC<{ state: PullToRefreshState; className?: string }> = ({
  state,
  className,
  children,
}) => {
  const armed = state.pull >= PULL_THRESHOLD;

  return (
    <div ref={state.containerRef} className={className} style={{ overscrollBehaviorY: "contain" }}>
      <div className="flex items-center justify-center overflow-hidden" style={{ height: state.pull }}>
        {!state.refreshing && state.pull > 0 && (
          <span className="pull-hint-bounce" style={{ opacity: Math.min(1, state.pull / PULL_THRESHOLD) }}>
            <PullDownIcon
              style={{
                fontSize: 22,
                color: "#fb923c",
                transform: armed ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 180ms ease",
              }}
            />
          </span>
        )}
      </div>
      {children}
    </div>
  );
};
